import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';
import {
  fireExcoAdEvent,
  fireExcoPlayerEvent,
  handleExcoSlotStart,
  removeExcoPlayer,
  setExcoPlayerData,
  updatePlayerData,
} from 'client/engagement-handlers/ads-engagement-handler/ads-engagement-handler';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';

import './exco-video.scss';

export function ExcoVideo({
  id,
  excoProps,
  customTargeting = null,
  showTestText = true,
  classes = null,
  styles = null,
  autoplay = null,
  playbackMode = '',
  playVideoPlaylist = null,
  playerReadyCallback = null,
}) {
  const excoRef = useRef();
  const playerApiRef = useRef(null);
  const intervalRef = useRef({});
  const EXCO_SCRIPT_TAG_ID = 'exco-video-init';
  const { mobile, edwcat, edwpg, edw, vid, zip, mk, mdl, sub, year, type, doNotSell, usPrivacy, sect1, sect2 } =
    excoProps;

  const dataExsConfig = useMemo(() => {
    const customParams = {
      mobile,
      edwcat,
      edwpg,
      edw,
      vid,
      zip,
      mk,
      mdl,
      sub,
      year,
      type,
      doNotSell,
      us_privacy: usPrivacy,
      sect1,
      sect2,
      ...customTargeting,
    };

    return { customParams };
  }, [
    customTargeting,
    mobile,
    edwcat,
    edwpg,
    edw,
    vid,
    zip,
    mk,
    mdl,
    sub,
    year,
    type,
    doNotSell,
    usPrivacy,
    sect1,
    sect2,
    autoplay,
    playbackMode,
  ]);

  // Handle page hide event for BFCache compatibility
  const handlePageHide = useCallback(() => {
    if (playerApiRef.current && typeof playerApiRef.current.destroy === 'function') {
      playerApiRef.current.destroy();
    }
  }, []);

  // Function to inject the ExCo script only once
  const injectExcoScript = () => {
    if (!document.getElementById(EXCO_SCRIPT_TAG_ID)) {
      const script = document.createElement('script');
      script.textContent = `(function(d,s,n){var js,fjs=d.getElementsByTagName(s)[0];js=d.createElement(s);js.className=n;js.src="//player.ex.co/player/${id}";js.setAttribute("programmatic","true");fjs.parentNode.insertBefore(js,fjs);}(document,"script","exco-player"));`;
      script.async = true;
      script.id = EXCO_SCRIPT_TAG_ID;
      document.head.appendChild(script);
    }
  };

  // Function to set up event listeners for ExCo player API
  const setupExcoEventListeners = () => {
    intervalRef.current.checkForPlayerApi = setInterval(() => {
      if (window.ExCoPlayer && !playerApiRef.current) {
        const playerApi = window.ExCoPlayer.connect(id);
        if (playerApi && playerApi.EVENTS) {
          clearInterval(intervalRef.current.checkForPlayerApi);
          playerApiRef.current = playerApi;

          const EVENTS = playerApi.EVENTS;

          // Named handlers required for symmetric off() cleanup
          const slotStartHandler = () => handleExcoSlotStart(id);

          // Special handlers with side effects beyond just tracking
          const excoPausedEvent = data => {
            fireExcoPlayerEvent(id, 'player-paused', data);
            updatePlayerData({ playerId: id, isPaused: true });
          };
          const excoPlayingEvent = data => {
            fireExcoPlayerEvent(id, 'player-playing', data);
            updatePlayerData({ playerId: id, isPaused: false });
            if (playerReadyCallback) {
              playerReadyCallback(playerApi.getPlaylist());
            }
          };
          const excoPlayerLoadedEvent = data => {
            fireExcoPlayerEvent(id, 'player-load', data);
            setExcoPlayerData({ playerId: id, playerApi, isPaused: false, userInitiatedPause: false });
          };

          // Ad events — all share the same handler shape: data => fireExcoAdEvent(id, name, data)
          const AD_EVENT_MAP = [
            [EVENTS.AD_IMPRESSION, 'ad-impression'],
            [EVENTS.AD_PLAYING, 'ad-play'],
            [EVENTS.AD_PAUSE, 'ad-pause'],
            [EVENTS.AD_FIRST_QUARTILE, 'ad-first-quartile'],
            [EVENTS.AD_MIDPOINT, 'ad-midpoint'],
            [EVENTS.AD_THIRD_QUARTILE, 'ad-third-quartile'],
            [EVENTS.AD_COMPLETED, 'ad-completed'],
            [EVENTS.AD_CLICKED, 'ad-clicked'],
            [EVENTS.AD_SKIPPED, 'ad-skipped'],
          ];

          // Map of EVENTS key -> tracking event name for all generic player events
          const GENERIC_PLAYER_EVENT_MAP = [
            [EVENTS.PLAYER_INIT, 'player-init'],
            [EVENTS.PLAYER_READY, 'player-ready'],
            [EVENTS.PLAYER_DESTROY, 'player-destroy'],
            [EVENTS.PLAYER_CLOSED, 'player-closed'],
            [EVENTS.PLAYER_MUTED, 'player-muted'],
            [EVENTS.PLAYER_UNMUTED, 'player-unmuted'],
            [EVENTS.PLAYER_STICKY, 'player-sticky'],
            [EVENTS.PLAYER_STATIC, 'player-static'],
            [EVENTS.ENTER_FULLSCREEN, 'enter-fullscreen'],
            [EVENTS.EXIT_FULLSCREEN, 'exit-fullscreen'],
            [EVENTS.PLAYER_IN, 'player-in'],
            [EVENTS.PLAYER_OUT, 'player-out'],
          ];

          // Build and register ad handlers in one pass
          const adHandlers = AD_EVENT_MAP.map(([event, eventName]) => {
            const handler = data => fireExcoAdEvent(id, eventName, data);
            playerApi.on(event, handler);
            return [event, handler];
          });

          // Build generic handlers and register them in one pass
          const genericPlayerHandlers = GENERIC_PLAYER_EVENT_MAP.map(([event, eventName]) => {
            const handler = data => fireExcoPlayerEvent(id, eventName, data);
            playerApi.on(event, handler);
            return [event, handler];
          });

          // Register special handlers with side effects
          playerApi.on(EVENTS.SLOT_START, slotStartHandler);
          playerApi.on(EVENTS.PLAYER_PAUSED, excoPausedEvent);
          playerApi.on(EVENTS.PLAYER_PLAYING, excoPlayingEvent);
          playerApi.on(EVENTS.PLAYER_LOAD, excoPlayerLoadedEvent);

          if (playVideoPlaylist) {
            playVideoPlaylist(playerApi);
          } else {
            playerApi.init({
              customParams: dataExsConfig.customParams,
              playbackMode,
              autoPlay: autoplay,
              mute: true,
            });
          }

          // Store all handlers for cleanup
          playerApiRef.current.registeredHandlers = [
            [EVENTS.SLOT_START, slotStartHandler],
            [EVENTS.PLAYER_PAUSED, excoPausedEvent],
            [EVENTS.PLAYER_PLAYING, excoPlayingEvent],
            [EVENTS.PLAYER_LOAD, excoPlayerLoadedEvent],
            ...adHandlers,
            ...genericPlayerHandlers,
          ];
        }
      }
    }, 100);

    // Cleanup after 15 seconds
    setTimeout(() => {
      clearInterval(intervalRef.current.checkForPlayerApi);
    }, 15000);
  };

  useEffect(() => {
    let timeoutId;

    if (get(excoRef, 'current')) {
      timeoutId = delayHeavyTask(() => {
        injectExcoScript();
        setupExcoEventListeners();
      });
    }

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('pagehide', handlePageHide);
      clearInterval(intervalRef.current.checkForPlayerApi);
      removeExcoPlayer(id);

      if (playerApiRef.current) {
        const playerApi = playerApiRef.current;

        if (playerApi.EVENTS && playerApi.registeredHandlers && typeof playerApi.off === 'function') {
          playerApi.registeredHandlers.forEach(([event, handler]) => {
            playerApi.off(event, handler);
          });
        }

        playerApi.destroy();
      }
      playerApiRef.current = null;
    };
  }, [id, handlePageHide]);

  return (
    <FeatureFlag name="enableExco">
      <div
        id={id}
        ref={excoRef}
        className={classnames('heading-4 exco-player', classes, {
          'pb-1': showTestText,
        })}
        style={styles}
      >
        {showTestText && <span className="pb-0_5 d-block">Testing EX.CO</span>}
      </div>
    </FeatureFlag>
  );
}

ExcoVideo.propTypes = {
  id: PropTypes.string.isRequired,
  excoProps: PropTypes.shape({
    mobile: PropTypes.bool,
    edwcat: PropTypes.string,
    edwpg: PropTypes.string,
    edw: PropTypes.string,
    vid: PropTypes.string,
    zip: PropTypes.string,
    mk: PropTypes.string,
    mdl: PropTypes.string,
    sub: PropTypes.string,
    year: PropTypes.string,
    type: PropTypes.string,
    doNotSell: PropTypes.bool,
    usPrivacy: PropTypes.string,
    sect1: PropTypes.string,
    sect2: PropTypes.string,
  }).isRequired,
  customTargeting: PropTypes.shape({}),
  showTestText: PropTypes.bool,
  classes: PropTypes.string,
  styles: PropTypes.shape({}),
  autoplay: PropTypes.bool,
  playbackMode: PropTypes.string,
  playVideoPlaylist: PropTypes.func,
  playerReadyCallback: PropTypes.func,
};
